/**
 * AssetDrawerModals Component
 *
 * Manages the modal components for the asset drawer.
 * Pure presentation component - all state management handled by parent.
 *
 * Note Entry Points:
 * - Annotate dropdown "Add Note" → Uses this modal with simple text mode
 * - Notes tab "New Note" button → Uses Notes component's inline markdown modal
 *
 * This separation provides:
 * - Quick text notes from Annotate dropdown (simple mode)
 * - Rich formatted notes from Notes tab (markdown mode with MarkdownEditor)
 */

import { NoteModal } from '@/components/modals';
import { TagModal } from '@/sections/asset/components/modals';
import { Asset } from '@/types';

export interface AssetDrawerModalsProps {
  asset: Asset;
  isCommentModalOpen: boolean;
  setIsCommentModalOpen: (open: boolean) => void;
  isTagModalOpen: boolean;
  setIsTagModalOpen: (open: boolean) => void;
  handleComment: (comment: string) => Promise<void>;
}

/**
 * AssetDrawerModals renders the modal components for asset actions:
 * - NoteModal: For adding notes/comments to assets (using shared component)
 * - TagModal: For managing asset tags
 *
 * This is a pure presentation component that passes through state and handlers.
 */
export function AssetDrawerModals({
  asset,
  isCommentModalOpen,
  setIsCommentModalOpen,
  isTagModalOpen,
  setIsTagModalOpen,
  handleComment,
}: AssetDrawerModalsProps) {
  return (
    <>
      <NoteModal
        open={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        onSubmit={handleComment}
        resourceType="Asset"
        mode="markdown"
        selectedCount={1}
        presentation="modal"
      />
      <TagModal
        assetKeys={[asset.key]}
        currentTags={asset?.tags || []}
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
      />
    </>
  );
}
